import { useState } from 'react'
import {
  CloseButton,
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Text,
  VStack,
  Wrap,
  Dialog,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiCheck, FiEdit2, FiTrash2 } from 'react-icons/fi'
import { useAskTemplates, useRenameAskTemplate, useDeleteAskTemplate } from '../../hooks/useLobbyDay'
import { Card, ResponsiveModal, StatusChip } from '../shared'
import { formatSyncedAgo, mostRecentRefresh } from './formatSyncedAgo'
import type { AskTemplateDto, AskPosition } from '../../types/lobbyDay'

interface AskTemplatePickerModalProps {
  open: boolean
  onClose: () => void
  onUse: (templateId: string) => void
}

function positionPalette(position: AskPosition) {
  return position === 'Support' ? 'green' : position === 'Oppose' ? 'red' : 'yellow'
}

interface TemplateRowProps {
  template: AskTemplateDto
  onUse: (id: string) => void
  onRename: (id: string, name: string) => void
  onDelete: (id: string) => void
  renaming: boolean
  deleting: boolean
}

function TemplateRow({ template, onUse, onRename, onDelete, renaming, deleting }: TemplateRowProps) {
  const [editing, setEditing] = useState(false)
  const [draftName, setDraftName] = useState(template.name)

  const syncedAgo = formatSyncedAgo(mostRecentRefresh(template.linkedBills.map((b) => b.lastRefreshedAt)))

  const startRename = () => {
    setDraftName(template.name)
    setEditing(true)
  }

  const commitRename = () => {
    const trimmed = draftName.trim()
    if (trimmed && trimmed !== template.name) onRename(template.id, trimmed)
    setEditing(false)
  }

  return (
    <Card p={3}>
      <VStack align="stretch" gap={2}>
        <Box>
          {editing ? (
            <HStack gap={2}>
              <Input
                size="sm"
                autoFocus
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename()
                  if (e.key === 'Escape') setEditing(false)
                }}
              />
              <Button size="sm" colorPalette="brand" minH="44px" onClick={commitRename} loading={renaming}>
                Save
              </Button>
            </HStack>
          ) : (
            <Text fontWeight="bold">{template.name}</Text>
          )}

          <HStack mt={1} gap={2} wrap="wrap" align="center">
            <Badge colorPalette={positionPalette(template.position)}>{template.position}</Badge>
            {template.billNumber && (
              <Text fontSize="sm" color="ink.2">{template.billNumber}</Text>
            )}
          </HStack>
        </Box>

        {template.linkedBills.length > 0 && (
          <Wrap gap={2}>
            {template.linkedBills.map((b) => (
              <StatusChip key={b.id} variant="neutral">{b.billNumber}</StatusChip>
            ))}
          </Wrap>
        )}

        {syncedAgo && (
          <Text fontSize="xs" color="ink.2">bill synced {syncedAgo}</Text>
        )}

        <HStack justify="flex-end" gap={2} wrap="wrap">
          <Button size="sm" variant="ghost" minH="44px" onClick={startRename}>
            <FiEdit2 /> Rename
          </Button>
          <Button
            size="sm"
            variant="ghost"
            colorPalette="red"
            minH="44px"
            onClick={() => onDelete(template.id)}
            loading={deleting}
          >
            <FiTrash2 /> Delete
          </Button>
          <Button size="sm" colorPalette="brand" minH="44px" onClick={() => onUse(template.id)}>
            <FiCheck /> Use
          </Button>
        </HStack>
      </VStack>
    </Card>
  );
}

export function AskTemplatePickerModal({ open, onClose, onUse }: AskTemplatePickerModalProps) {
  const { data: templates, isLoading } = useAskTemplates()
  const renameMutation = useRenameAskTemplate()
  const deleteMutation = useDeleteAskTemplate()

  const handleRename = (templateId: string, name: string) => {
    renameMutation.mutate(
      { templateId, name },
      { onError: () => toaster.create({ type: 'error', title: 'Failed to rename template' }) },
    )
  }

  const handleDelete = (templateId: string) => {
    deleteMutation.mutate(templateId, {
      onError: () => toaster.create({ type: 'error', title: 'Failed to delete template' }),
    })
  }

  const items = templates ?? []

  return (
    <ResponsiveModal isOpen={open} onClose={onClose} size="lg">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>Ask templates</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            <VStack align="stretch" gap={3}>
              {isLoading && (
                <Text color="ink.2" fontSize="sm" textAlign="center" py={6}>
                  Loading templates…
                </Text>
              )}

              {!isLoading && items.length === 0 && (
                <Text color="ink.2" fontSize="sm" textAlign="center" py={8}>
                  No saved templates yet. Save an ask as a template to reuse it in future events.
                </Text>
              )}

              {items.map((t) => (
                <TemplateRow
                  key={t.id}
                  template={t}
                  onUse={onUse}
                  onRename={handleRename}
                  onDelete={handleDelete}
                  renaming={renameMutation.isPending}
                  deleting={deleteMutation.isPending}
                />
              ))}
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
